/*
  User-360 ACL tab body — read-only list of current ACL rows.
*/
import React, { useEffect, useState } from 'react';
import { Key } from 'lucide-react';
import { itgcService, UserAclRow } from '../../services/itgcService';

interface Props { userId: number; }

const UserAclPanel: React.FC<Props> = ({ userId }) => {
  const [rows, setRows] = useState<UserAclRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [showRevoked, setShowRevoked] = useState(false);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        setRows(await itgcService.listUserAcl(userId, showRevoked));
      } catch (e) {
        console.error(e);
      } finally { setLoading(false); }
    })();
  }, [userId, showRevoked]);

  if (loading) {
    return <div className="flex items-center justify-center h-32 text-slate-400 text-sm">Loading ACL…</div>;
  }

  const apps = rows.filter(r => r.target_type === 'APPLICATION');
  const assets = rows.filter(r => r.target_type === 'ASSET');

  return (
    <>
      <div className="flex items-center justify-between mb-2">
        <p className="text-xs text-slate-500 -mt-1 mb-0">
          ITGC Access Control List — read-only view of applications and assets currently granted to this user.
          Managed from Organisation → ITGC Access tab.
        </p>
        <label className="text-xs flex items-center gap-1 text-slate-600">
          <input type="checkbox" checked={showRevoked}
                 onChange={e => setShowRevoked(e.target.checked)}
                 data-testid="CHK_itgc_acl_include_revoked" />
          Include revoked
        </label>
      </div>

      {rows.length === 0 ? (
        <div className="flex flex-col items-center justify-center h-32 text-slate-400 text-sm">
          <Key className="w-8 h-8 mb-2 opacity-30" />
          <span>No ACL entries for this user.</span>
        </div>
      ) : (
        <div className="space-y-4">
          <AclSection title="Applications" rows={apps} />
          <AclSection title="Assets" rows={assets} />
        </div>
      )}
    </>
  );
};

const AclSection: React.FC<{ title: string; rows: UserAclRow[] }> = ({ title, rows }) => {
  if (rows.length === 0) return null;
  return (
    <div>
      <h4 className="text-xs font-semibold text-slate-600 uppercase tracking-wide mb-1">{title}</h4>
      <div className="border border-slate-200 rounded-lg overflow-hidden">
        <table className="w-full text-xs">
          <thead className="bg-slate-50 text-slate-600">
            <tr>
              <th className="text-left px-3 py-2">Target</th>
              <th className="text-left px-3 py-2">Granted on</th>
              <th className="text-left px-3 py-2">Validity</th>
              <th className="text-left px-3 py-2">Status</th>
              <th className="text-left px-3 py-2">Revoked on</th>
            </tr>
          </thead>
          <tbody>
            {rows.map(r => (
              <tr key={r.id} className="border-t border-slate-100">
                <td className="px-3 py-2">{r.target_label || `#${r.target_id}`}</td>
                <td className="px-3 py-2">{r.granted_on ? new Date(r.granted_on).toLocaleDateString() : '—'}</td>
                <td className="px-3 py-2">
                  {r.valid_from || r.valid_until
                    ? `${r.valid_from || '—'} → ${r.valid_until || '—'}`
                    : '—'}
                </td>
                <td className="px-3 py-2">
                  {r.is_active === 1
                    ? <span className="itgc-badge itgc-badge-green">ACTIVE</span>
                    : <span className="itgc-badge itgc-badge-grey">REVOKED</span>}
                </td>
                <td className="px-3 py-2">{r.revoked_on ? new Date(r.revoked_on).toLocaleDateString() : '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default UserAclPanel;
